import { GLOSSARY, type GlossaryKey } from '../../../school_panel/lib/glossary';
import { Segmented, Switch, Table, Td, Term, Th } from '../../../school_panel/components/ui';
import { fieldClass } from '../../../school_panel/lib/fieldClass';
// src/pages/Exams/generate/MoreOptions.tsx
// Absorbs the former AdvancedSections.tsx, StyleProfilePanel.tsx and the
// summary half of LiveSummary.tsx into one plain disclosure panel.
import type { Blueprint, Difficulty } from './types';
import { useSchoolChapters } from './hooks/useSchoolChapters';
import { useBookList } from './hooks/useBookList';
import { gradeLabel } from '../../../school_panel/lib/gradeLabel';
import { useNationalExams } from './hooks/useNationalExams';
import { usePrediction } from './hooks/usePrediction';
import { PredictionPanel } from './PredictionPanel';
import { StyleProfileSummary } from './StyleProfileSummary';

interface Props {
  blueprint: Blueprint;
  patch: (patch: Partial<Blueprint>) => void;
}

/** Shared look for the set-up form's inputs and selects (the panel-wide field class). */
export const GEN_INPUT = fieldClass;

const CELL_INPUT =
  'min-h-[36px] rounded-[12px] border border-line-strong bg-surface px-2.5 py-1 text-sm text-ink ' +
  'focus:outline-2 focus:outline-offset-1 focus:outline-accent';

const FORMS = [1, 2, 3, 4];

// Design `.sec-t`: a 15px display-font section title.
function Section({ title, help, children }: { title: string; help?: GlossaryKey | string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="mb-2.5 font-display text-[15px] font-bold text-ink">{help ? <Term {...(help in GLOSSARY ? { k: help as GlossaryKey } : { def: help })}>{title}</Term> : title}</h4>
      {children}
    </section>
  );
}

export function MoreOptions({ blueprint, patch }: Props) {
  const { data: chapters } = useSchoolChapters(blueprint.subject_id);
  const { data: books } = useBookList(blueprint.subject_id);
  const { data: nes } = useNationalExams();
  const prediction = usePrediction(blueprint.national_exam_id, blueprint.subject_id, blueprint.total_questions);
  const neName = nes?.find(n => n.id === blueprint.national_exam_id)?.name ?? 'national exam';

  const updateChapterCount = (chapterId: number, count: number) => {
    const existing = blueprint.chapter_distribution.find(e => e.chapter_id === chapterId);
    const next = existing
      ? blueprint.chapter_distribution.map(e =>
          e.chapter_id === chapterId ? { ...e, count: Math.max(0, count) } : e)
      : [...blueprint.chapter_distribution,
         { chapter_id: chapterId, count: Math.max(0, count), difficulty_override: null }];
    patch({ chapter_distribution: next });
  };

  const setChapterIncluded = (chapterId: number, included: boolean) => {
    const existing = blueprint.chapter_distribution.find(e => e.chapter_id === chapterId);
    let next;
    if (!existing) {
      next = included
        ? [...blueprint.chapter_distribution,
           { chapter_id: chapterId, count: 1, difficulty_override: null }]
        : blueprint.chapter_distribution;
    } else {
      next = blueprint.chapter_distribution.map(e =>
        e.chapter_id === chapterId ? { ...e, count: included ? Math.max(1, e.count) : 0 } : e);
    }
    patch({ chapter_distribution: next });
  };

  const updateChapterDifficulty = (chapterId: number, diff: Difficulty | '') => {
    const next = blueprint.chapter_distribution.map(e =>
      e.chapter_id === chapterId
        ? { ...e, difficulty_override: diff === '' ? null : diff }
        : e
    );
    patch({ chapter_distribution: next });
  };

  const toggleBook = (bookId: number) => {
    const has = blueprint.book_ids_included.includes(bookId);
    const next = has
      ? blueprint.book_ids_included.filter(id => id !== bookId)
      : [...blueprint.book_ids_included, bookId];
    patch({ book_ids_included: next });
  };

  return (
    <div className="mt-4 flex flex-col gap-5">
      <Section title="Chapters" help="Which chapters to draw questions from, how many per chapter, and an optional difficulty per chapter.">
        <Table minWidth={520}>
          <thead>
            <tr>
              <Th className="w-16">Include</Th>
              <Th>Chapter</Th>
              <Th align="right" className="w-28">Questions</Th>
              <Th className="w-36">Difficulty</Th>
            </tr>
          </thead>
          <tbody>
            {/* The exam grade's chapters, plus any picked from other grades on the main form. */}
            {(chapters ?? []).filter(c => blueprint.grade_id == null
              || (c.school_grade_ids ?? []).length === 0
              || (c.school_grade_ids ?? []).includes(blueprint.grade_id)
              || (blueprint.chapter_distribution.find(e => e.chapter_id === c.id)?.count ?? 0) > 0).map(c => {
              const entry = blueprint.chapter_distribution.find(e => e.chapter_id === c.id);
              const count = entry?.count ?? 0;
              const included = count > 0;
              return (
                <tr key={c.id} className={included ? '' : 'opacity-60'}>
                  <Td>
                    <Switch tone="navy" checked={included} label={`Include ${c.name}`} onChange={(next) => setChapterIncluded(c.id, next)} />
                  </Td>
                  <Td className="font-medium text-ink">{c.name}</Td>
                  <Td align="right">
                    <input
                      type="number" min={0} max={50}
                      aria-label={`Questions from ${c.name}`}
                      className={`${CELL_INPUT} ml-auto w-20 text-right font-mono`}
                      value={count}
                      onChange={(e) => updateChapterCount(c.id, Number(e.target.value) || 0)}
                    />
                  </Td>
                  <Td>
                    <select
                      aria-label={`Difficulty for ${c.name}`}
                      className={CELL_INPUT}
                      value={entry?.difficulty_override ?? ''}
                      onChange={(e) => updateChapterDifficulty(
                        c.id, e.target.value as Difficulty | '')}
                    >
                      <option value="">Global</option>
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Section>

      <Section title="Book sources" help="book_sources">
        <p className="mb-2.5 text-[13px] text-muted">Questions are grounded in these textbooks. The exam grade's book is selected first; add other grades' books when the exam covers them.</p>
        <div className="flex flex-wrap gap-2">
          {[...(books ?? [])].sort((a, b) => {
            const mine = (x: typeof a) => (blueprint.grade_id != null && (x.school_grade_ids ?? []).includes(blueprint.grade_id) ? 0 : 1);
            return mine(a) - mine(b);
          }).map(b => {
            const on = blueprint.book_ids_included.includes(b.id);
            return (
              <button key={b.id} type="button" aria-pressed={on} onClick={() => toggleBook(b.id)}
                className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-[13px] text-left text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                  on
                    ? 'border-navy bg-navy text-white dark:border-mist-fixed dark:bg-mist-fixed dark:text-navy'
                    : 'border-line-strong bg-surface text-ink hover:border-slate'}`}>
                {b.title}{b.grade ? <span className={on ? 'opacity-80' : 'text-muted'}>{' · '}{gradeLabel(b.grade)}</span> : null}
              </button>
            );
          })}
          {(!books || books.length === 0) && (
            <span className="text-xs text-muted">No books found for this subject.</span>
          )}
        </div>
      </Section>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Section title="Predict a national exam" help="mirror_national">
        <select
          id="gen-national-exam"
          aria-label="Predict a national exam"
          className={GEN_INPUT}
          value={blueprint.national_exam_id ?? ''}
          onChange={(e) => patch({
            national_exam_id: e.target.value ? Number(e.target.value) : null,
            chapter_distribution: [],
          })}
          disabled={!blueprint.subject_id}
        >
          <option value="">None</option>
          {nes?.map(ne => (
            <option key={ne.id} value={ne.id}>{ne.name}</option>
          ))}
        </select>
        <p className="mt-1.5 text-xs text-muted">Forecasts the next paper from eltest-school's past papers and matches their style.</p>
        <StyleProfileSummary
          neId={blueprint.national_exam_id}
          subjectId={blueprint.subject_id}
        />
      </Section>

      <Section title="Number of forms" help="forms">
        <Segmented<string>
          label="Number of forms"
          options={FORMS.map(n => ({ key: String(n), label: String(n) }))}
          value={String(blueprint.num_forms)}
          onChange={(k) => patch({ num_forms: Number(k) })}
        />
        <p className="mt-1.5 text-xs text-muted">Parallel versions with different questions.</p>
      </Section>
      </div>

      {blueprint.national_exam_id && blueprint.subject_id && (
        <Section title={`Predicted next ${neName}`} help="mirror_national">
          <PredictionPanel prediction={prediction.data} loading={prediction.isLoading} examName={neName} patch={patch} />
        </Section>
      )}
    </div>
  );
}
